import React from 'react';
import { Table } from '@mantine/core';
import type { AccountantReportView } from 'shared-models/browser';
import { formatMoney, formatReportDate } from './accountant-report.utils';
import { isolateAmountLtr } from '../../../../utils/money';
import classes from './accountant-report-table.module.scss';

export type AccountantReportTableProps = {
  view: AccountantReportView;
};

// Summary: RTL accountant preview table — merged group bands, dynamic brand columns, totals row.
const AccountantReportTableComponent: React.FC<AccountantReportTableProps> = ({ view }) => {
  const { cardBrands, rows, totals } = view;
  const paymentColSpan = 6 + cardBrands.length + 1;
  const money = (value: number): string => isolateAmountLtr(formatMoney(value));

  return (
    <div className={classes.tableWrap} dir="rtl">
      <Table className={classes.table} withTableBorder withColumnBorders striped>
        <Table.Thead className={classes.head}>
          <Table.Tr>
            <Table.Th rowSpan={2} className={classes.cellText}>תאריך</Table.Th>
            <Table.Th rowSpan={2} className={classes.cellText}>מספר Z</Table.Th>
            <Table.Th colSpan={3} className={classes.groupHeader}>סך הכל מכירות</Table.Th>
            <Table.Th colSpan={paymentColSpan} className={classes.groupHeader}>סוגי תשלום</Table.Th>
            <Table.Th rowSpan={2} className={classes.cellNum}>עודף טיפ</Table.Th>
          </Table.Tr>
          <Table.Tr>
            <Table.Th className={classes.cellNum}>מכירות נטו</Table.Th>
            <Table.Th className={classes.cellNum}>מע"מ</Table.Th>
            <Table.Th className={classes.cellNum}>סה"כ מכירות</Table.Th>
            <Table.Th className={classes.cellNum}>מזומן</Table.Th>
            <Table.Th className={classes.cellNum}>המחאות</Table.Th>
            <Table.Th className={classes.cellNum}>לקוח קיים</Table.Th>
            <Table.Th className={classes.cellNum}>תן ביס</Table.Th>
            <Table.Th className={classes.cellNum}>העברה בנקאית</Table.Th>
            {cardBrands.map((brand) => (
              <Table.Th key={brand} className={classes.cellNum}>{brand}</Table.Th>
            ))}
            <Table.Th className={classes.cellNum}>אחר</Table.Th>
            <Table.Th className={classes.cellNum}>סה"כ הכנסות</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => (
            <Table.Tr key={row.sessionId}>
              <Table.Td className={classes.cellText}>{formatReportDate(row.date)}</Table.Td>
              <Table.Td className={classes.cellText}>{row.sessionName}</Table.Td>
              <Table.Td className={classes.cellNum}>{money(row.net)}</Table.Td>
              <Table.Td className={classes.cellNum}>{money(row.vat)}</Table.Td>
              <Table.Td className={classes.cellNum}>{money(row.gross)}</Table.Td>
              <Table.Td className={classes.cellNum}>{money(row.cash)}</Table.Td>
              <Table.Td className={classes.cellNum}>{money(row.checks)}</Table.Td>
              <Table.Td className={classes.cellNum}>{money(row.onAccount)}</Table.Td>
              <Table.Td className={classes.cellNum}>{money(row.tenbis)}</Table.Td>
              <Table.Td className={classes.cellNum}>{money(row.bankTransfer)}</Table.Td>
              {cardBrands.map((brand) => (
                <Table.Td key={brand} className={classes.cellNum}>{money(row.byBrand[brand] ?? 0)}</Table.Td>
              ))}
              <Table.Td className={classes.cellNum}>{money(row.other)}</Table.Td>
              <Table.Td className={classes.cellNum}>{money(row.totalIncome)}</Table.Td>
              <Table.Td className={classes.cellNum}>{money(row.tips)}</Table.Td>
            </Table.Tr>
          ))}
          {rows.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={6 + paymentColSpan} className={classes.empty}>אין משמרות בטווח שנבחר</Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
        {rows.length > 0 && (
          <Table.Tfoot className={classes.totals}>
            <Table.Tr>
              <Table.Th className={classes.cellText}>סה"כ</Table.Th>
              <Table.Th className={classes.cellText} />
              <Table.Th className={classes.cellNum}>{money(totals.net)}</Table.Th>
              <Table.Th className={classes.cellNum}>{money(totals.vat)}</Table.Th>
              <Table.Th className={classes.cellNum}>{money(totals.gross)}</Table.Th>
              <Table.Th className={classes.cellNum}>{money(totals.cash)}</Table.Th>
              <Table.Th className={classes.cellNum}>{money(totals.checks)}</Table.Th>
              <Table.Th className={classes.cellNum}>{money(totals.onAccount)}</Table.Th>
              <Table.Th className={classes.cellNum}>{money(totals.tenbis)}</Table.Th>
              <Table.Th className={classes.cellNum}>{money(totals.bankTransfer)}</Table.Th>
              {cardBrands.map((brand) => (
                <Table.Th key={brand} className={classes.cellNum}>{money(totals.byBrand[brand] ?? 0)}</Table.Th>
              ))}
              <Table.Th className={classes.cellNum}>{money(totals.other)}</Table.Th>
              <Table.Th className={classes.cellNum}>{money(totals.totalIncome)}</Table.Th>
              <Table.Th className={classes.cellNum}>{money(totals.tips)}</Table.Th>
            </Table.Tr>
          </Table.Tfoot>
        )}
      </Table>
    </div>
  );
};

export const AccountantReportTable = React.memo(AccountantReportTableComponent);
